import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import {
  AuthoredIcon,
  type AuthoredIconSize,
} from '@/components/admin/shared/authored-icon'
import { hasAuthoredIcon } from '@/lib/icons'

/** Only the fields the stack reads — both tables pass richer source rows. */
interface StackableSource {
  id: string
  sourceIcon?: string | null
}

/**
 * The overlapping badge stack a table's Name column shows for a problem with
 * several attributed sources.
 *
 * Sources with no icon are skipped rather than given a generic mark: a row of
 * identical `Code` glyphs would say less than an honestly shorter stack. That
 * makes the empty case the one to get right — when *every* source lacks an
 * icon the stack has nothing to draw, and a bare `<span/>` would leave the row
 * with no mark at all (and, where the caller wraps the mark in a link button,
 * a 0×0 focusable target). So the filter runs before the branch, and a stack
 * with nothing in it falls through to a single mark exactly as a problem with
 * no sources does.
 *
 * Shared by the problems and system-design tables, which draw the same stack
 * and differ only in whether they have one more icon value to try
 * (`fallbackIcon`) before reaching the glyph.
 *
 * Takes `fallback` as a `LucideIcon` and a `size` rung rather than a
 * ready-made node, matching `AuthoredIcon` — the two sit in this directory
 * exporting a prop of the same name, so opposite contracts would be a trap.
 * It is also what lets the glyph be sized here instead of at the call site,
 * where one table was hand-writing the `md` rung as `h-6 w-6`.
 */
export function SourceIconStack({
  sources,
  fallback,
  fallbackIcon,
  size,
}: {
  sources: readonly StackableSource[] | null | undefined
  /** Glyph of last resort, once no icon value in the row can render. */
  fallback: LucideIcon
  /**
   * One more icon value to try when the stack is empty — the problem's own
   * source icon. Omitted by callers that have nothing further to offer.
   */
  fallbackIcon?: string | null
  size?: AuthoredIconSize
}): ReactNode {
  // `hasAuthoredIcon`, not a raw truthiness test: a whitespace-only value is
  // truthy but renders as a fallback, so testing it here would admit a source
  // to the stack and then draw the generic mark this component exists to keep
  // out of it.
  const iconed = sources?.filter((s) => hasAuthoredIcon(s.sourceIcon)) ?? []

  if (iconed.length === 0) {
    return (
      <AuthoredIcon
        icon={fallbackIcon}
        size={size}
        className="text-muted-foreground shrink-0 rounded"
        fallback={fallback}
      />
    )
  }

  return (
    <span className="flex shrink-0 items-center -space-x-1">
      {iconed.map((s) => (
        <AuthoredIcon
          key={s.id}
          icon={s.sourceIcon}
          size={size}
          className="text-muted-foreground rounded"
          fallback={fallback}
        />
      ))}
    </span>
  )
}
